import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/authContext';
import { adminNavigationItems } from './adminNavigation';
import './AdminShell.css';

function AdminShell({ eyebrow, title, description, meta = [], primaryAction, secondaryAction, children }) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const activeItem = useMemo(
    () => adminNavigationItems.find((item) => location.pathname === item.path),
    [location.pathname]
  );

  const handleNavigate = (path) => {
    setIsSidebarOpen(false);
    navigate(path);
  };

  const handleLogout = () => {
    setIsSidebarOpen(false);
    logout();
    navigate('/');
  };

  return (
    <div className="admin-shell">
      <div
        className={`admin-shell-overlay ${isSidebarOpen ? 'active' : ''}`}
        onClick={() => setIsSidebarOpen(false)}
        aria-hidden="true"
      />

      <aside className={`admin-shell-sidebar ${isSidebarOpen ? 'active' : ''}`}>
        <div className="admin-shell-sidebar-header">
          <span className="admin-shell-sidebar-kicker">Latthsaab Network</span>
          <h1 className="admin-shell-sidebar-title">Admin Control</h1>
          <p className="admin-shell-sidebar-subtitle">Professional editorial workspace for content, videos, headlines, and location coverage.</p>
        </div>

        <nav className="admin-shell-nav">
          {adminNavigationItems.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`admin-shell-nav-item ${activeItem?.key === item.key ? 'active' : ''}`}
              onClick={() => handleNavigate(item.path)}
            >
              <span className="admin-shell-nav-icon">{item.accent}</span>
              <span className="admin-shell-nav-copy">
                <span className="admin-shell-nav-label">{item.label}</span>
                <span className="admin-shell-nav-description">{item.description}</span>
              </span>
            </button>
          ))}
        </nav>

        <div className="admin-shell-sidebar-footer">
          <div className="admin-shell-user-card">
            <div className="admin-shell-user-avatar">{(user?.name || 'A').charAt(0).toUpperCase()}</div>
            <div className="admin-shell-user-copy">
              <p className="admin-shell-user-name">{user?.name || 'Admin'}</p>
              <p className="admin-shell-user-email">{user?.email || 'admin@example.com'}</p>
            </div>
          </div>
          <button type="button" className="admin-shell-logout-btn" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </aside>

      <main className="admin-shell-main">
        <header className="admin-shell-topbar">
          <div className="admin-shell-topbar-copy">
            <button
              type="button"
              className="admin-shell-menu-toggle"
              onClick={() => setIsSidebarOpen((open) => !open)}
              aria-label="Toggle admin navigation"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div>
              <p className="admin-shell-eyebrow">{eyebrow}</p>
              <h2 className="admin-shell-title">{title}</h2>
              {description ? <p className="admin-shell-description">{description}</p> : null}
            </div>
          </div>

          <div className="admin-shell-topbar-actions">
            {secondaryAction ? (
              <button type="button" className="admin-shell-btn secondary" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </button>
            ) : null}
            {primaryAction ? (
              <button type="button" className="admin-shell-btn primary" onClick={primaryAction.onClick}>
                {primaryAction.label}
              </button>
            ) : null}
          </div>
        </header>

        <section className="admin-shell-hero-card">
          <div>
            <span className="admin-shell-kicker">Focused workspace</span>
            <h3 className="admin-shell-hero-title">{activeItem?.label || title}</h3>
            <p className="admin-shell-hero-text">Keep the admin experience consistent across every route with a clearer hierarchy, cleaner forms, and faster navigation.</p>
          </div>

          {meta.length > 0 ? (
            <div className="admin-shell-meta-grid">
              {meta.map((item) => (
                <div key={item.label} className="admin-shell-meta-card">
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          ) : null}
        </section>

        <div className="admin-shell-content">{children}</div>
      </main>
    </div>
  );
}

export default AdminShell;